


























import * as Moment from "moment";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { DomainRegistry } from "../../../../../domain";
import { DateDuration } from "../../../../../domain/system";
import { Patient } from "../../../../../domain/patient";
import { DocumentSummary, DocumentSearchParameter, DocumentTypeKinds } from "../../../../../domain/document";
import { DocumentsSort } from "../../../../document/selectors";
import { ISortParameter } from "../../../../system";
import { DocumentSelector } from "../../../selectors/wizard/guest";

@Component({
  components: {
    DocumentsSort,
    DocumentSelector,
  }
})
export default class DocumentListViewer extends Mixins() {
  @Prop()
  patient: Patient;

  @Prop({default: false})
  shouldAuthenticateLater: boolean;

  data(): any {
    return {
      isLoading: false,
      foundDocuments: [] as DocumentSummary[],
      sortDocuments: [] as DocumentSummary[],
      sortParameter: null,
      selectedDocument: null,
    };
  }
  isLoading: boolean;
  foundDocuments: DocumentSummary[];
  sortDocuments: DocumentSummary[];
  sortParameter: ISortParameter;
  selectedDocument: DocumentSummary;

  async mounted(): Promise<void> {
    this.registerEvent();
    if (this.patient) {
      await this.updateFoundDocuments();
    }
  }

  destroyed(): void {
    window.removeEventListener("message", this.onChildWindowNotify, false);
  }

  get patientId(): string {
    return this.patient
      ? this.patient.id
      : "";
  }

  get userId(): string {
    return this.$store.getters.user
      ? this.$store.getters.user.id
      : "";
  }

  get departmentId(): string {
    return this.$store.getters.department
      ? this.$store.getters.department.id
      : "";
  }

  get isDocumentDateDuration(): boolean {
    return this.$configuration.behavior.document.list.patientSpecified.finder.duration.default.isDocument;
  }

  get defaultDocumentSearchParameter(): DocumentSearchParameter {
    const parameter = new DocumentSearchParameter();
    parameter.patientId = this.patientId;
    parameter.fromDate = this.toYyyyMmDd(new DateDuration(null, null).from);
    parameter.toDate = this.toYyyyMmDd(new DateDuration(null, null).to);
    parameter.durationName = this.toDurationKindString(this.isDocumentDateDuration);
    parameter.departmentId = null;
    parameter.containsDeleted = false;
    parameter.containsTemporary = true;
    parameter.containsRegistered = true;
    parameter.documentKindIds = [];
    parameter.loginUserId = this.userId;
    parameter.contentDateSearch = this.isDocumentDateDuration;
    parameter.loginUserOnly = false;
    parameter.formatName = null;
    parameter.formatIds = null;
    parameter.manageNumberText = null;
    parameter.containsVirtualDocuments = false;
    parameter.updateUserCodes = [];
    return parameter;
  }

  get isEditable(): boolean {
    return this.$store.getters.userAuthority && this.$store.getters.userAuthority.documentEditable;
  }

  get isEditButtonEnable(): boolean {
    return this.isDocumentWritable && !this.isDocumentDeleted && !this.isDocumentFixed;
  }

  get isDocumentNormal(): boolean {
    return this.selectedDocument && (this.selectedDocument.documentType === DocumentTypeKinds.Normal);
  }
  
  get isDocumentWritable(): boolean {
    return this.selectedDocument && this.selectedDocument.isWritable && this.isDocumentNormal;
  }

  get isDocumentDeleted(): boolean {
    return this.selectedDocument && this.selectedDocument.isDeleted;
  }
  
  get isDocumentFixed(): boolean {
    return this.selectedDocument && this.selectedDocument.isFixed;
  }

  private toYyyyMmDd(source: Date): string {
    return !source
      ? null
      : Moment(source).format("YYYYMMDD");
  }
  private toDurationKindString(source: boolean): string {
    return source ? "文書日付" : "更新日";
  }

  private registerEvent(): void {
    window.addEventListener("message", this.onChildWindowNotify, false);
  }

  async showDocument(): Promise<void> {
    if (!this.selectedDocument || !this.selectedDocument.isReadable) { return; }
    const hasAuthority = await this.hasAuthority();
    if (!hasAuthority) { return; }

    this.$openWindow(
      "/Wizard/Guest/ShowDocument",
      {
        patientNo: this.selectedDocument.patientNo,
        documentId: this.selectedDocument.id.toString(),
        isReadRestrictionAlreadyHandled: true.toString(),
      }
    );
  }

  async editDocument(): Promise<void> {
    if (!this.selectedDocument) { return; }
    const hasAuthority = await this.hasAuthority();
    if (!hasAuthority) { return; }

    this.$openWindow(
      "/Wizard/Guest/EditDocument",
      {
        patientNo: this.patientId,
        documentId: this.selectedDocument.id.toString(),
        shouldAuthenticateLater: this.shouldAuthenticateLater.toString(),
        isReadRestrictionAlreadyHandled: true.toString(),
      }
    );
  }

  async hasAuthority(): Promise<boolean> {
    try {
      // TODO: 現状では参照不可の文書でここに到達することは無いようだが、isReadableの判定もしておいたほうがよい？
      const isConfirmed = await this.isConfirmedReadable(this.selectedDocument);
      if (!isConfirmed) { return false; }
      await this.checkLogin(this.selectedDocument);
      await this.checkWriteAuditLog(this.selectedDocument);
      return true;
    } catch (e) {
      return false;
    }
  }

  private async isConfirmedReadable(row: DocumentSummary): Promise<boolean> {
    if (!row.shouldConfirmReadable) { return true; }
    const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
      null,
      row.shouldWriteAuditLog
        ? ["この書類の閲覧は許可されていません。閲覧履歴は管理者に通知されます。", "処理を続行しますか？"]
        : ["この書類の閲覧は許可されていません。処理を続行しますか？"],
      "続行する"
    );
    return (index === 0);
  }
  
  private async checkLogin(row: DocumentSummary): Promise<void> {
    if (row.shouldLogin) {
      await this.$commonDialogs.authentication.authenticator.show();
    }
  }

  private async checkWriteAuditLog(row: DocumentSummary): Promise<void> {
    if (row.shouldWriteAuditLog) {
      const service = DomainRegistry.document.getDocumentRepository();
      try {
        await service.registerAuditLog(row);
      } catch (e) {
        this.$error(
          "閲覧履歴通知失敗",
          "閲覧履歴通知でエラーが発生しました。",
          e,
        );
        throw e;
      }
    }
  }

  onSortChanged(param: ISortParameter): void {
    this.sortParameter = param;
  }

  onDocumentDoubleClicked(): void {
    this.showDocument();
  }

  async getWizardDocuments(): Promise<DocumentSummary[]> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      const documents =  await repository.searchWizardDocuments(this.defaultDocumentSearchParameter);
      return documents;
    } catch (e) {
      this.$error("ウィザード文書一覧取得失敗", "Wizard文書一覧取得に失敗しました。", e);
    }
  }

  async updateFoundDocuments(): Promise<void> {
    this.isLoading = true;
    this.foundDocuments = await this.getWizardDocuments();
    this.isLoading = false;
  }

  async onChildWindowNotify(event: any): Promise<void> {
    if (!event || !event.data) { return; }
    if (event.data.length === 0 || typeof event.data !== "string") { return; }

    const message = JSON.parse(event.data);
    this.$logger.debug("postMessage受信");
    console.log(message);

    if (message.key === "documentRegistered") {
      this.$logger.info("文書が登録されました");
      await this.updateFoundDocuments();
    }
  }

  @Watch("patient")
  async onPatientUpdated(newValue: Patient, oldValue: Patient): Promise<void> {
    if (newValue && newValue !== oldValue) {
      await this.updateFoundDocuments();
    }
  }
}
